import React, { useMemo, useState } from "react";
import MonacoEditor from "react-monaco-editor";
import * as monacoEditor from 'monaco-editor/esm/vs/editor/editor.api';
import { format } from 'sql-formatter';
import 'monaco-editor/esm/vs/basic-languages/monaco.contribution';
import 'monaco-editor/esm/vs/editor/edcore.main';
import 'monaco-editor/esm/vs/language/typescript/monaco.contribution';
import 'monaco-editor/esm/vs/editor/standalone/browser/standaloneServices';
import 'monaco-editor/esm/vs/language/json/monaco.contribution';
import { ClearIcon, CopyIcon } from "../../assets/icons";
import { editorOptions } from "../form/editor";
import { infoAlert } from "../toastify/notify-toast";
import styles from "./styles.module.scss";
import { TooltipTop } from "../tooltips";
import { Tooltip } from "antd";


type CustomMonacoEditorControllerProps = {
    initialValue ?: string;
}

type useCustomMonacoEditorController = (props?: CustomMonacoEditorControllerProps) => {
    editorValue: string;
    setEditorValue: (arg0: string) => void;
    clearEditor: () => void;
    copyText: () => void;
}

const useCustomMonacoEditorController: useCustomMonacoEditorController = ({ initialValue = '' } = { initialValue: ''}) => {
    const [editorValue, setEditorValue] = useState(initialValue);


    const copyText = () => {
        const el = document.createElement('textarea');
        el.value = editorValue;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        infoAlert('Copied to Clipboard.');
    };

    const clearEditor = () => {
        setEditorValue('');
    };

    return({
        editorValue,
        setEditorValue,
        clearEditor,
        copyText
    })
}

type ToolbarOptions = 'Copy' | 'Clear' | 'Format' | 'Maximize'

type CustomMonacoEditorProps = ReturnType<useCustomMonacoEditorController> & {
    language ?: string;
    editorHeight ?: string | number;
    editorWidth ?: string | number;
    children ?: React.ReactNode;
    readOnly ?: boolean;
    toolbarOptions ?: ToolbarOptions[];
    label ?: string;
    required ?: boolean;
    infoText ?: string;
    setShowModal ?: (arg0: string, arg1: boolean) => void;
}



const CustomMonacoEditor = ({  label = undefined, required = false, infoText = undefined, editorWidth = '100%', editorHeight = '100%', toolbarOptions = ['Clear', 'Copy', 'Format', 'Maximize'] , readOnly = false, editorValue, setEditorValue, copyText, clearEditor, children, language, setShowModal }: CustomMonacoEditorProps) => {

    const handleEditorDidMount = (editor: monacoEditor.editor.IStandaloneCodeEditor, monaco: typeof monacoEditor) => {
        monaco.editor.defineTheme('ds', {
            base: 'vs-dark',
            inherit: true,
            rules: [],
            colors: {
                'editor.foreground': '#14182a',
                'editor.background': '#14182a',
                'editor.lineHighlightBackground': '#14182a',
            }
        });
        monaco.editor.setTheme('ds');
    };

    

    const memoizedEditorOptions = useMemo(() => {
        return {...editorOptions, readOnly }
    }, [readOnly])

    const { showClear, showCopy, showFormat, showMaximize } = useMemo(() =>{
        let showCopy = false;
        let showClear = false;
        let showFormat = false;
        let showMaximize = false;
        
        toolbarOptions.forEach(option => {
            if(option === 'Clear') showClear = true;
            else if(option === 'Copy') showCopy = true;
            else if(option === 'Format') showFormat = true;
            else if(option === 'Maximize') showMaximize = true;
        })

        return { showClear, showCopy, showFormat, showMaximize }
    }, [toolbarOptions])


    return(
        <div className='queryEditorMaster'>
            {!!label && 
                <label 
                    className="inputfield__label"
                >
                    <span className={required ? 'red-star': ''}>{label}</span>
                    {!!infoText &&
                        <TooltipTop placement="topRight" overlay={infoText}>
                            <img src="/icons/info-fields.png" width="16" height="16" className="info__icon" alt="information-icon" />
                        </TooltipTop>
                    }
                </label>
            }
            <div className={styles["editorHeader"]}>
                {showCopy && 
                    <button
                        onClick={copyText}
                        type="button"
                    >
                        <CopyIcon /> Copy
                    </button>
                }
                {showClear && 
                    <button
                        onClick={clearEditor}
                        type="button"
                    >
                        <ClearIcon /> Clear
                    </button>
                }
                {showFormat && 
                    <span 
                        className="maximize-img" 
                        onClick={()=>{
                            setEditorValue(format(editorValue));
                            infoAlert('Format Complete');
                        }}>
                            <Tooltip
                                key={'max-editor1'}
                                title={'Format'}
                                placement="bottom"
                                overlayClassName="tab__toolTip"
                            >
                                <span style={{
                                    color: '#6d6f7a',
                                    fontSize: 15
                                }}>
                                    {'{;}'}
                                </span>
                            </Tooltip>
                    </span>
                }
                {showMaximize &&
                    <span className="maximize-img" id="editor-max"
                     onClick={()=>setShowModal?.(editorValue || '', true)}
                    >
                        <Tooltip
                            key={'max-editor'}
                            title={'Maximize'}
                            placement="bottom"
                            overlayClassName="tab__toolTip"
                        >
                            <img width="20px" src="/icons/maximize-modal.png" alt="" />
                        </Tooltip>
                    </span>
                }
            </div>
            <div className={styles["editor"]}>
            <MonacoEditor
                height={editorHeight}
                width={editorWidth}
                language={language}
                theme={'vs-dark'}
                value={editorValue}
                options={memoizedEditorOptions}
                onChange={setEditorValue}
                editorDidMount={handleEditorDidMount}
            />
            </div>
            {children}
        </div>
    )
}

CustomMonacoEditor.useCustomMonacoEditorController = useCustomMonacoEditorController;


export default CustomMonacoEditor;